<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" hide-details />
            </template>
            <template #default>
              <v-row>
                <v-col sm="5">
                  <select-list
                    v-model="query.params.employmentType"
                    :items="employmentTypeOptions"
                    label="Employment Type"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="7">
                  <select-list
                    v-model="query.params.department"
                    :items="departmentOptions"
                    label="Department"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="5">
                  <select-list
                    v-model="query.params.console_roles"
                    :items="mappedRoles"
                    label="Role"
                    clearable
                    multiple
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>
            <template #content>
              <data-table
                :headers="tableHeaders"
                :items="users"
                :server-items-length="pagination.total"
                :page="pagination.currentPage"
                :items-per-page="pagination.perPage"
                :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, -1] }"
                no-data-text="No users found"
                disable-sort
                @pagination="$_queryHandler_onUpdatePagination"
              >
                <template #item="{ item }">
                  <td>
                    <v-row no-gutters align="center">
                      <v-col class="flex-grow-0">
                        <item-logo :name="item.name" class="ml-0 mr-3" />
                      </v-col>
                      <v-col>
                        <div>
                          <div class="font-weight-medium">{{ item.name }}</div>
                          <div class="grey--text text--darken-1">
                            {{ item.email }}
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </td>
                  <td>
                    {{ getOptionKeyByValue(employmentTypeOptions, item.employment_type) }}
                  </td>
                  <td>
                    {{ getOptionKeyByValue(departmentOptions, item.department) }}
                  </td>
                  <td>
                    <v-tooltip max-width="200" bottom>
                      <template #activator="{ on }">
                        <role-chip :value="String(item.console_roles.length)" v-on="on" />
                      </template>
                      <span>{{ item.console_roles.map(nameForRole).join(', ') }}</span>
                    </v-tooltip>
                  </td>
                </template>
                <template #actions="{ item }">
                  <v-list-item :disabled="!isPermittedTo('update')" @click="onClickEdit(item)">
                    <v-list-item-title>
                      <v-icon left small>edit</v-icon>
                      <span>Edit</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="!isPermittedTo('delete') || me.sub === item.sub" @click="onClickDelete(item)">
                    <v-list-item-title>
                      <v-icon left small>delete</v-icon>
                      <span>Delete</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <fab-create v-if="isPermittedTo('create')" type="User" @click="showCreateModal = true" />

    <!-- Create Modal -->
    <generic-modal
      v-if="currentUser && showCreateModal"
      v-model="showCreateModal"
      :is-disabled="isLoading || !currentUserComplete"
      :is-loading="isLoading"
      title="Create User"
      persistent
      @modal-confirm="onModalCreateConfirm"
      @modal-cancel="onModalCreateCancel"
    >
      <v-row>
        <v-col sm="6">
          <text-input v-model="currentUser.name" label="Name" hide-details />
          <text-input v-model="currentUser.email" class="mt-6" label="Email Address" hide-details />
          <autocomplete-input
            v-model="currentUser.employment_type"
            :items="employmentTypeOptions"
            class="mt-6"
            label="Employment Type"
            hide-details
          />
          <autocomplete-input
            v-model="currentUser.department"
            :items="departmentOptions"
            class="mt-6"
            label="Department"
            hide-details
          />
          <autocomplete-input
            v-model="currentUser.console_roles"
            :items="mappedRoles"
            class="mt-6"
            label="Roles"
            hide-details
            multiple
          />
        </v-col>
      </v-row>
    </generic-modal>

    <!-- Edit Modal -->
    <generic-modal
      v-if="currentUser && showEditModal"
      v-model="showEditModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Edit User"
      persistent
      @modal-confirm="onModalEditConfirm"
      @modal-cancel="onModalEditCancel"
    >
      <v-row>
        <v-col sm="6">
          <text-input v-model="currentUser.name" label="Name" hide-details />
          <autocomplete-input
            v-model="currentUser.employment_type"
            :items="employmentTypeOptions"
            class="mt-6"
            label="Employment Type"
            hide-details
          />
          <autocomplete-input
            v-model="currentUser.department"
            :items="departmentOptions"
            class="mt-6"
            label="Department"
            hide-details
          />
          <autocomplete-input
            v-model="currentUser.console_roles"
            :items="mappedRoles"
            class="mt-6"
            label="Roles"
            hide-details
            multiple
          />
        </v-col>
      </v-row>
    </generic-modal>

    <delete-modal
      v-if="currentUser && showDeleteModal"
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :item="currentUser.name"
      category="User"
      @modal-confirm="onModalDeleteConfirm"
      @modal-cancel="onModalDeleteCancel"
    />
  </page-loader>
</template>

<script>
// Helpers
import { mapGetters, mapMutations } from 'vuex'
import { cloneDeep } from 'lodash-es'
import getOptionKeyByValue from '@/utils/get-option-key-by-value'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import DeleteModal from '@/components/modals/delete-modal'
import FabCreate from '@/components/fab-create'
import GenericModal from '@/components/modals/generic-modal'
import ItemLogo from '@/components/item-logo'
import MoreMenu from '@/components/more-menu'
import PageLoader from '@/components/page-loader'
import RoleChip from '@/components/chips/role'
import SearchInput from '@/components/forms/search-input'
import TextInput from '@/components/forms/text-input'
import FilterAccordion from '@/components/filter-accordion'
import SelectList from '@/components/forms/select-list'
import DataTable from '@/components/data-table'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { users } from '@/services/offers'

const resetCurrentUser = () => ({
  name: null,
  email: null,
  console_roles: [],
})

export default {
  name: 'UserManagement',
  components: {
    AutocompleteInput,
    DeleteModal,
    FabCreate,
    GenericModal,
    ItemLogo,
    PageLoader,
    RoleChip,
    SearchInput,
    TextInput,
    FilterAccordion,
    SelectList,
    DataTable,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    searchKey: 'name',
    users: [],
    console_roles: [],
    currentUser: resetCurrentUser(),
    pagination: {
      currentPage: 1,
      lastPage: null,
      perPage: 20,
      total: 0,
    },
    showCreateModal: false,
    showEditModal: false,
    showDeleteModal: false,
    tableHeaders: [
      { text: 'Name', value: 'name' },
      { text: 'Employment Type', value: 'employment_type' },
      { text: 'Department', value: 'department' },
      { text: 'Roles', value: 'console_roles' },
      { text: '', value: 'actions', align: 'end' },
    ],
    queriesDefined: ['employmentType', 'department', 'console_roles'],
    queryTypes: { console_roles: 'array' },
  }),
  computed: {
    ...mapGetters('account', { me: 'user' }),
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    ...mapGetters('options', ['getItemsForOption', 'hasOptions']),
    hasErrors() {
      return !this.hasOptions || this.hasPageErrors
    },
    mappedRoles() {
      return this.console_roles.map(({ slug, name }) => ({ key: name || slug, value: slug }))
    },
    currentUserComplete() {
      return this.currentUser.name && this.currentUser.email
    },
    departmentOptions() {
      return this.getItemsForOption('departments')
    },
    employmentTypeOptions() {
      return this.getItemsForOption('employmentTypes')
    },
  },
  async created() {
    await Promise.all([this.getUsers(), this.getUserRoles()])
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    getOptionKeyByValue,
    async getUserRoles() {
      this.isLoading = true

      try {
        const roles = await users.getUserRoles()
        this.console_roles = roles
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve user roles',
        })
      }

      this.setPageLoading(false)
      this.isLoading = false
    },
    async getUsers() {
      this.isLoading = true
      const { perPage, currentPage: page } = this.pagination

      try {
        const {
          data,
          pagination: { total },
        } = await users.getFilteredUsers({
          filters: this.queryFilters,
          page: perPage !== -1 ? page : 1,
          perPage: perPage !== -1 ? perPage : null,
          orders: [{ column: 'name' }],
        })

        this.users = data || []
        this.pagination.total = total
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve users',
        })
      }

      this.setPageLoading(false)
      this.isLoading = false
    },
    async createUser(user) {
      this.isLoading = true

      try {
        await users.createUser(this.currentUser)
        this.$notify({ type: 'success', text: 'User successfully created' })
        await this.getUsers()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to create user',
        })
      }

      this.isLoading = false
    },
    async updateUser(user) {
      this.isLoading = true

      try {
        await users.updateUser(user.sub, user)
        this.$notify({
          type: 'success',
          text: 'Successfully updated user',
        })
        await this.getUsers()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to update user',
        })
      }

      this.isLoading = false
    },
    async deleteUser(user) {
      this.isLoading = true

      try {
        await users.deleteUser(user.sub)
        this.$notify({
          type: 'success',
          text: 'Successfully removed user',
        })
        await this.getUsers()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete user',
        })
      }

      this.isLoading = false
    },
    async handleQueryUpdate() {
      await this.getUsers()
    },
    async onPageChange(page) {
      this.pagination.currentPage = page
      await this.getUsers()
    },
    nameForRole(role) {
      const roleData = this.console_roles.find(({ slug }) => slug === role)
      return (roleData && roleData.name) || role
    },
    onClickEdit(user) {
      this.currentUser = cloneDeep(user)
      this.showEditModal = true
    },
    async onModalEditConfirm() {
      await this.updateUser(this.currentUser)
      this.showEditModal = false
      this.currentUser = resetCurrentUser()
    },
    onModalEditCancel() {
      this.showEditModal = false
      this.currentUser = resetCurrentUser()
    },
    onClickDelete(user) {
      this.currentUser = cloneDeep(user)
      this.showDeleteModal = true
    },
    async onModalDeleteConfirm() {
      await this.deleteUser(this.currentUser)
      this.showDeleteModal = false
      this.currentUser = resetCurrentUser()
    },
    onModalDeleteCancel() {
      this.showDeleteModal = false
      this.currentUser = resetCurrentUser()
    },
    async onModalCreateConfirm() {
      await this.createUser(this.currentUser)
      this.showCreateModal = false
      this.currentUser = resetCurrentUser()
    },
    onModalCreateCancel() {
      this.showCreateModal = false
      this.currentUser = resetCurrentUser()
    },
    onTablePerPageUpdate(perPage) {
      this.pagination.perPage = perPage
      this.getUsers()
    },
    onTablePageUpdate(page) {
      this.pagination.currentPage = page
      this.getUsers()
    },
  },
}
</script>

<style lang="scss" scoped>
.role-chip-wrapper {
  max-width: 300px;
}
</style>
